/**
 * AuditLogPage — read-only met filters, paginatie, client-side CSV-export.
 *
 * Endpoint: GET /api/admin/audit-log
 *   query: user_id, actie, entiteit_type, entiteit_id, from, to, include_db_mutations, limit, offset
 *   Default: include_db_mutations=false (alleen applicatie-acties)
 */
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminGet, formatApiError } from "../../lib/adminApi";
import { PageHeader, ErrorBanner, OvergeslagenBanner, LoadingRow, EmptyRow, formatDate, PaginationBar } from "../../components/admin/AdminUI";
import { AUDIT_ACTIE_NAMEN, AUDIT_ENTITEIT_NAMEN, noemNetjes } from "../../lib/namen";

const PAGE_SIZE = 50;

/* Het filter liet je vroeger de kale sleutel typen ("login_success" — een actie die niet
   eens bestond). Nu kies je de naam die ook in de kolom staat. */
const ACTIE_KEUZES = Object.entries(AUDIT_ACTIE_NAMEN).sort((a, b) => a[1].localeCompare(b[1], "nl"));

function toCsv(rows) {
  const cols = ["created_at", "user_id", "actie", "entiteit_type", "entiteit_id", "ip_address", "user_agent"];
  const header = cols.join(",");
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = rows.map((r) => cols.map((c) => escape(r[c])).join(","));
  return [header, ...lines].join("\n");
}

function downloadCsv(rows) {
  const csv = toCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AuditLogPage() {
  const [filters, setFilters] = useState({
    user_id: "",
    actie: "",
    entiteit_type: "",
    from: "",
    to: "",
    include_db_mutations: false,
  });
  const [offset, setOffset] = useState(0);

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE, offset, include_db_mutations: filters.include_db_mutations };
    if (filters.user_id) p.user_id = filters.user_id;
    if (filters.actie) p.actie = filters.actie;
    if (filters.entiteit_type) p.entiteit_type = filters.entiteit_type;
    if (filters.from) p.from = new Date(filters.from).toISOString();
    if (filters.to) p.to = new Date(filters.to + "T23:59:59").toISOString();
    return p;
  }, [filters, offset]);

  const q = useQuery({
    queryKey: ["audit-log-list", params],
    queryFn: () => adminGet("/api/admin/audit-log", params),
    keepPreviousData: true,
  });
  const items = q.data?.items || [];
  const total = q.data?.total ?? 0;

  function updateFilter(k, v) { setOffset(0); setFilters((s) => ({ ...s, [k]: v })); }

  return (
    <div data-testid="audit-log-page">
      <PageHeader title="Audit-log" subtitle="Alle applicatie-acties met optionele DB-mutaties (AVG-trail)" />
      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="regel" meervoud="regels"
        testid="auditlog-overgeslagen"
      />

      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm mb-3" data-testid="audit-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-700">User-ID</span>
            <input data-testid="filter-user-id" value={filters.user_id} onChange={(e) => updateFilter("user_id", e.target.value)}
              placeholder="UUID" className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-mono min-h-[40px]" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-700">Actie</span>
            <select data-testid="filter-actie" value={filters.actie} onChange={(e) => updateFilter("actie", e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]">
              <option value="">— alle —</option>
              {ACTIE_KEUZES.map(([waarde, naam]) => (
                <option key={waarde} value={waarde}>{naam}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-700">Entiteit-type</span>
            <select data-testid="filter-entiteit-type" value={filters.entiteit_type} onChange={(e) => updateFilter("entiteit_type", e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]">
              <option value="">— alle —</option>
              {Object.keys(AUDIT_ENTITEIT_NAMEN).map((t) => (
                <option key={t} value={t}>{AUDIT_ENTITEIT_NAMEN[t]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-700">Vanaf</span>
            <input type="date" data-testid="filter-from" value={filters.from} onChange={(e) => updateFilter("from", e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-700">Tot/met</span>
            <input type="date" data-testid="filter-to" value={filters.to} onChange={(e) => updateFilter("to", e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]" />
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="filter-include-db-mutations"
              checked={filters.include_db_mutations}
              onChange={(e) => updateFilter("include_db_mutations", e.target.checked)} />
            <span className="text-sm text-slate-800">Toon ook DB-mutaties (INSERT/UPDATE/DELETE)</span>
          </label>
          <div className="flex gap-2">
            <button type="button" data-testid="btn-audit-reset"
              onClick={() => { setOffset(0); setFilters({ user_id: "", actie: "", entiteit_type: "", from: "", to: "", include_db_mutations: false }); }}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm min-h-[40px]">Reset filters</button>
            <button type="button" data-testid="btn-audit-csv"
              onClick={() => downloadCsv(items)} disabled={items.length === 0}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 min-h-[40px]">
              CSV downloaden ({items.length})
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-700">
            <tr>
              <th className="px-4 py-2 text-left">Wanneer</th>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-left">Actie</th>
              <th className="px-4 py-2 text-left">Entiteit</th>
              <th className="px-4 py-2 text-left">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.isLoading ? <LoadingRow cols={5} /> :
             items.length === 0 ? <EmptyRow cols={5} /> :
             items.map((a) => (
              <tr key={a.log_id} data-testid={`audit-row-${a.log_id}`}>
                <td className="px-4 py-2 text-xs text-slate-700">{formatDate(a.created_at)}</td>
                <td className="px-4 py-2 text-xs font-mono">{a.user_id ? a.user_id.slice(0, 8) + "…" : "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-900" title={a.actie || ""}>{noemNetjes(a.actie, AUDIT_ACTIE_NAMEN)}</td>
                <td className="px-4 py-2 text-xs" title={a.entiteit_type || ""}>{noemNetjes(a.entiteit_type, AUDIT_ENTITEIT_NAMEN)}{a.entiteit_id ? ` / ${a.entiteit_id.slice(0, 8)}…` : ""}</td>
                <td className="px-4 py-2 text-xs text-slate-700">{a.ip_address || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationBar offset={offset} limit={PAGE_SIZE} total={total}
        onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
        onNext={() => setOffset(offset + PAGE_SIZE)} />
    </div>
  );
}
